// Central theme configuration - SERA (Saudi Electricity Regulatory Authority) branding
export const theme = {
  colors: {
    // SERA brand colors
    primary: '#1B8C3C',        // Main teal
    primaryDark: '#006200',    // Darker teal
    secondary: '#0D1B2A',      // Dark navy
    secondaryLight: '#1A2744', // Lighter navy
    accent: '#1D7A5F',         // Green accent (buttons)
    accentLight: '#2A9D7E',    // Lighter green

    // Teal variations
    teal: '#1B8C3C',
    tealLight: '#4FB8B8',
    tealDark: '#006200',

    // Status colors
    success: '#1D7A5F',
    successDark: '#156B52',
    warning: '#D4A84B',
    warningDark: '#B8923E',
    error: '#C53030',

    // Text colors
    textPrimary: '#0D1B2A',
    textSecondary: '#1A2744',
    textMuted: '#4A5568',
    textLight: '#718096',
    textOnDark: '#FFFFFF',
    textOnTeal: '#FFFFFF',

    // Background colors
    backgroundLight: '#F0F7F7',
    backgroundGray: '#E8F0F0',
    backgroundWhite: '#FFFFFF',
    backgroundMuted: '#F5FAFA',
    backgroundDark: '#0D1B2A',
    backgroundNavy: '#1A2744',

    // Border colors
    borderLight: '#D0E0E0',
    borderMuted: '#B8CECE',
    borderDark: '#1B8C3C'
  },

  gradients: {
    primary: 'linear-gradient(135deg, #1B8C3C 0%, #006200 100%)',
    secondary: 'linear-gradient(135deg, #0D1B2A 0%, #1A2744 100%)',
    teal: 'linear-gradient(135deg, #4FB8B8 0%, #1B8C3C 100%)',
    success: 'linear-gradient(135deg, #1D7A5F 0%, #156B52 100%)',
    warning: 'linear-gradient(135deg, #D4A84B 0%, #B8923E 100%)',
    background: 'linear-gradient(135deg, #F0F7F7 0%, #E8F0F0 100%)',
    hero: 'linear-gradient(180deg, rgba(13, 27, 42, 0.8) 0%, rgba(27,140,60, 0.6) 100%)',
    subtle: 'linear-gradient(135deg, #F5FAFA 0%, #E8F0F0 100%)'
  },

  shadows: {
    card: '0 4px 20px rgba(13, 27, 42, 0.08)',
    elevated: '0 8px 32px rgba(13, 27, 42, 0.12)',
    button: '0 4px 12px rgba(27,140,60, 0.3)',
    subtle: '0 1px 3px rgba(13, 27, 42, 0.1)',
    focus: '0 0 0 2px rgba(27,140,60, 0.2)'
  },

  borderRadius: {
    small: '6px',
    medium: '8px',
    large: '12px',
    xl: '20px',
    round: '50%'
  },

  spacing: {
    xs: '4px',
    sm: '8px',
    md: '12px',
    lg: '16px',
    xl: '20px',
    xxl: '24px',
    xxxl: '32px'
  },

  typography: {
    fontFamily: {
      primary: "'Inter', 'Noto Sans Arabic', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      code: "'Fira Code', 'Monaco', 'Consolas', monospace"
    },
    fontSize: {
      xs: '11px',
      sm: '12px',
      md: '14px',
      lg: '16px',
      xl: '18px',
      xxl: '24px',
      xxxl: '28px'
    },
    fontWeight: {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700
    },
    lineHeight: {
      tight: 1.4,
      normal: 1.5,
      relaxed: 1.6
    }
  },

  breakpoints: {
    mobile: '480px',
    tablet: '768px',
    desktop: '1024px',
    wide: '1200px'
  },

  zIndex: {
    dropdown: 1000,
    modal: 2000,
    tooltip: 3000
  },

  transitions: {
    fast: '0.15s ease',
    normal: '0.2s ease',
    slow: '0.3s ease'
  }
};

// Helper functions for styled-components
export const getColor = (colorPath) => (props) => {
  const keys = colorPath.split('.');
  let value = props.theme || theme;
  for (const key of keys) {
    value = value[key];
  }
  return value;
};

export const getGradient = (gradientName) => (props) => {
  return (props.theme?.gradients || theme.gradients)[gradientName];
};

export const getShadow = (shadowName) => (props) => {
  return (props.theme?.shadows || theme.shadows)[shadowName];
};

// Pre-built styled component mixins
export const mixins = {
  flexCenter: `
    display: flex;
    align-items: center;
    justify-content: center;
  `,
  
  flexBetween: `
    display: flex;
    align-items: center;
    justify-content: space-between;
  `,
  
  absoluteFill: `
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  `,
  
  visuallyHidden: `
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  `,
  
  truncateText: `
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `,
  
  customScrollbar: `
    &::-webkit-scrollbar {
      width: 8px;
      height: 8px;
    }
    
    &::-webkit-scrollbar-track {
      background: rgba(0, 0, 0, 0.1);
      border-radius: 4px;
    }
    
    &::-webkit-scrollbar-thumb {
      background: rgba(27,140,60, 0.3);
      border-radius: 4px;
      transition: background 0.2s ease;
    }

    &::-webkit-scrollbar-thumb:hover {
      background: rgba(27,140,60, 0.5);
    }
  `
};